/**
 * Button — editorial flat surfaces. No box-shadow, no border-radius, no
 * bouncy easing. Variants per UX §8.4.
 *   primary      filled ink-900 / bg text on hover ink-700
 *   secondary    hairline ink-300, transparent fill, hover surface-2
 *   ghost        no border, hover surface-2
 *   destructive  oxblood-tinted border, transparent fill, fills accent on hover
 *
 * Loading: button label dims to 60% and a hairline scrubs underneath.
 * Sizes: sm | md | lg
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** When true, the button is in a loading state — label dims, hairline scrubs. */
  loading?: boolean;
  /** Optional loading label override (defaults to children with shimmer). */
  loadingLabel?: string;
  /** Renders as a Slot — useful for wrapping a Link without nesting buttons. */
  asChild?: boolean;
  children?: React.ReactNode;
}

const BASE = cn(
  'inline-flex items-center justify-center gap-2',
  'font-sans uppercase tracking-[0.12em] font-medium',
  'transition-[background-color,color,border-color,opacity]',
  'duration-200 ease-out-slow',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
  'disabled:opacity-40 disabled:pointer-events-none',
  // No border-radius — editorial flat. No box-shadow.
  'rounded-none',
  'relative select-none',
);

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-xs',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-sm',
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary: cn(
    'text-bg bg-ink-900 border border-ink-900',
    'hover:bg-ink-700 hover:border-ink-700',
  ),
  secondary: cn(
    'text-ink-900 bg-transparent border border-ink-300',
    'hover:border-ink-700 hover:bg-surface-2',
  ),
  ghost: cn(
    'text-ink-900 bg-transparent border border-transparent',
    'hover:bg-surface-2',
  ),
  destructive: cn(
    'text-accent bg-transparent border border-accent/40',
    'hover:bg-accent hover:text-bg hover:border-accent',
  ),
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingLabel,
      asChild = false,
      className,
      disabled,
      children,
      type,
      ...rest
    },
    ref,
  ) {
    const Comp = asChild ? Slot : 'button';
    const finalDisabled = disabled || loading;
    const labelClassName = cn(loading && 'opacity-60');

    // Buttons by default `type="button"` — only an explicit form-submitter
    // should be `type="submit"`. Without this, nested buttons inside forms
    // accidentally submit.
    const buttonType = asChild ? undefined : (type ?? 'button');

    return (
      <Comp
        ref={ref}
        type={buttonType}
        disabled={finalDisabled}
        aria-busy={loading || undefined}
        className={cn(BASE, SIZES[size], VARIANTS[variant], className)}
        {...rest}
      >
        <span className={labelClassName}>
          {loading && loadingLabel ? loadingLabel : children}
        </span>
        {loading ? (
          <span
            aria-hidden
            className="absolute left-0 right-0 bottom-0 h-px overflow-hidden"
          >
            <span className="block h-full w-1/3 bg-current opacity-50 animate-button-scrub" />
          </span>
        ) : null}
      </Comp>
    );
  },
);
